/* eslint-disable prefer-arrow-callback */

const config = window.cpAiqWallet || {};
const strings = config.strings || {};
const SESSION_KEY = config.sessionKey || 'cp_aiq_wallet_session';

function escapeHtml(value) {
	return String(value ?? '')
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#039;');
}

function formatPoints(points) {
	const n = Number(points) || 0;
	return n.toLocaleString();
}

function formatPhoneDisplay(phone) {
	const digits = String(phone || '').replace(/\D+/g, '');
	if (digits.length === 10) {
		return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
	}
	if (digits.length === 11 && digits.startsWith('1')) {
		return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
	}
	return phone || '';
}

async function apiRequest(path, options = {}) {
	const base = (config.restUrl || '').replace(/\/?$/, '/');
	const url = new URL(base + path.replace(/^\//, ''), window.location.origin);

	if (options.query) {
		Object.entries(options.query).forEach(([key, value]) => {
			if (value != null && value !== '') {
				url.searchParams.set(key, value);
			}
		});
	}

	const headers = {
		Accept: 'application/json',
		'Content-Type': 'application/json',
	};

	if (config.nonce) {
		headers['X-WP-Nonce'] = config.nonce;
	}

	const response = await fetch(url.toString(), {
		method: options.method || 'GET',
		headers,
		credentials: 'same-origin',
		body: options.body ? JSON.stringify(options.body) : undefined,
	});

	let data = null;
	try {
		data = await response.json();
	} catch (e) {
		data = null;
	}

	if (!response.ok) {
		const message =
			(data && (data.message || (data.data && data.data.message))) ||
			strings.genericError ||
			'Something went wrong.';
		const error = new Error(message);
		error.code = data && data.code ? data.code : 'aiq_error';
		error.status = response.status;
		error.data = data && data.data ? data.data : data;
		throw error;
	}

	return data;
}

function getStoredSession() {
	try {
		return localStorage.getItem(SESSION_KEY) || '';
	} catch (e) {
		return '';
	}
}

function setStoredSession(token) {
	try {
		if (token) {
			localStorage.setItem(SESSION_KEY, token);
		} else {
			localStorage.removeItem(SESSION_KEY);
		}
	} catch (e) {
		// ignore
	}
}

function createWalletApp(root) {
	const title = root.dataset.title || strings.title || 'Loyalty Wallet';
	const intro =
		root.dataset.intro ||
		strings.intro ||
		"Check your loyalty point balance and account details. Enter the phone number on your loyalty account and we'll text you a one-time code to sign in securely.";
	const joinLabel = root.dataset.joinLabel || strings.joinCta || 'Join loyalty';
	const joinUrl = root.dataset.joinUrl || config.joinUrl || '';
	const shopLabel = root.dataset.shopLabel || strings.shopCta || 'Shop & use your points';
	const shopUrl = root.dataset.shopUrl || config.shopUrl || '';
	const configured =
		root.dataset.configured === '1' || config.configured === true;

	const state = {
		step: configured ? 'phone' : 'unavailable',
		phone: '',
		code: '',
		wallet: null,
		sessionToken: getStoredSession(),
		loading: false,
		error: '',
		joinUrl,
	};

	function setState(patch) {
		Object.assign(state, patch);
		render();
	}

	function renderError() {
		if (!state.error) {
			return '';
		}

		let joinLink = '';
		if (
			(state.errorCode === 'aiq_not_enrolled' || state.errorCode === 'aiq_verify_failed') &&
			state.joinUrl
		) {
			joinLink = `<p class="aiq-wallet__join"><a class="btn btn-primary" href="${escapeHtml(
				state.joinUrl
			)}">${escapeHtml(joinLabel)}</a></p>`;
		}

		return `<div class="aiq-wallet__error" role="alert">${escapeHtml(state.error)}${joinLink}</div>`;
	}

	function renderPhoneStep() {
		return `
			<p class="aiq-wallet__intro">${escapeHtml(intro)}</p>
			<form class="aiq-wallet__form" data-aiq-form="phone" novalidate>
				<label class="aiq-wallet__label" for="${escapeHtml(root.id)}-phone">${escapeHtml(
			strings.phoneLabel || 'Phone number'
		)}</label>
				<input
					class="aiq-wallet__input"
					id="${escapeHtml(root.id)}-phone"
					name="phone"
					type="tel"
					autocomplete="tel"
					inputmode="tel"
					placeholder="${escapeHtml(strings.phonePlaceholder || '')}"
					value="${escapeHtml(state.phone)}"
					required
				/>
				${renderError()}
				<button class="btn btn-primary aiq-wallet__submit" type="submit" ${
					state.loading ? 'disabled' : ''
				}>
					${escapeHtml(state.loading ? strings.sending || 'Sending…' : strings.sendCode || 'Send code')}
				</button>
			</form>
		`;
	}

	function renderPinStep() {
		return `
			<form class="aiq-wallet__form" data-aiq-form="pin" novalidate>
				<p class="aiq-wallet__hint">Enter the code sent to ${escapeHtml(
					formatPhoneDisplay(state.phone)
				)}.</p>
				<label class="aiq-wallet__label" for="${escapeHtml(root.id)}-code">${escapeHtml(
			strings.codeLabel || 'Verification code'
		)}</label>
				<input
					class="aiq-wallet__input"
					id="${escapeHtml(root.id)}-code"
					name="code"
					type="text"
					inputmode="numeric"
					autocomplete="one-time-code"
					maxlength="8"
					placeholder="${escapeHtml(strings.codePlaceholder || '')}"
					value="${escapeHtml(state.code)}"
					required
				/>
				${renderError()}
				<button class="btn btn-primary aiq-wallet__submit" type="submit" ${
					state.loading ? 'disabled' : ''
				}>
					${escapeHtml(state.loading ? strings.loading || 'Loading…' : strings.verify || 'View wallet')}
				</button>
				<div class="aiq-wallet__actions">
					<button type="button" class="btn-reset aiq-wallet__link" data-aiq-action="resend" ${
						state.loading ? 'disabled' : ''
					}>${escapeHtml(strings.resend || 'Resend code')}</button>
					<button type="button" class="btn-reset aiq-wallet__link" data-aiq-action="back" ${
						state.loading ? 'disabled' : ''
					}>${escapeHtml(strings.back || 'Use a different number')}</button>
				</div>
			</form>
		`;
	}

	function renderWalletStep() {
		const wallet = state.wallet || {};
		const name = [wallet.firstName, wallet.lastName].filter(Boolean).join(' ').trim();
		const pointsBlock = wallet.hidePointsInWallet
			? `<p class="aiq-wallet__points-hidden">${escapeHtml(
					strings.pointsHidden || 'Points are not displayed for this program.'
			  )}</p>`
			: `<div class="aiq-wallet__points">
					<span class="aiq-wallet__points-label">${escapeHtml(
						strings.pointsLabel || 'Point balance'
					)}</span>
					<span class="aiq-wallet__points-value">${escapeHtml(formatPoints(wallet.loyaltyPoints))}</span>
				</div>`;

		const details = [];
		if (name) {
			details.push(
				`<div class="aiq-wallet__detail"><dt>${escapeHtml(
					strings.nameLabel || 'Name'
				)}</dt><dd>${escapeHtml(name)}</dd></div>`
			);
		}
		if (wallet.phone) {
			details.push(
				`<div class="aiq-wallet__detail"><dt>${escapeHtml(
					strings.phoneDetailLabel || 'Phone'
				)}</dt><dd>${escapeHtml(formatPhoneDisplay(wallet.phone))}</dd></div>`
			);
		}
		if (wallet.email) {
			details.push(
				`<div class="aiq-wallet__detail"><dt>${escapeHtml(
					strings.emailLabel || 'Email'
				)}</dt><dd>${escapeHtml(wallet.email)}</dd></div>`
			);
		}

		const shopCta = shopUrl
			? `<div class="aiq-wallet__ctas">
					<a class="btn btn-primary aiq-wallet__shop" href="${escapeHtml(shopUrl)}">${escapeHtml(
					shopLabel
			  )}</a>
				</div>`
			: '';

		const appleUrl = wallet.applePassUrl || '';
		const googleUrl = wallet.googlePassUrl || '';
		const appleBadge = config.appleBadgeUrl || '';
		const googleBadge = config.googleBadgeUrl || '';
		let passBadges = '';

		if (appleUrl || googleUrl) {
			const badges = [];
			if (appleUrl && appleBadge) {
				badges.push(
					`<a class="aiq-wallet__pass-link" href="${escapeHtml(
						appleUrl
					)}" download target="_blank" rel="noopener noreferrer">
						<img class="aiq-wallet__pass-badge aiq-wallet__pass-badge--apple" src="${escapeHtml(
							appleBadge
						)}" alt="${escapeHtml(strings.applePassAlt || 'Add to Apple Wallet')}" width="111" height="35" />
					</a>`
				);
			}
			if (googleUrl && googleBadge) {
				badges.push(
					`<a class="aiq-wallet__pass-link" href="${escapeHtml(
						googleUrl
					)}" target="_blank" rel="noopener noreferrer">
						<img class="aiq-wallet__pass-badge aiq-wallet__pass-badge--google" src="${escapeHtml(
							googleBadge
						)}" alt="${escapeHtml(strings.googlePassAlt || 'Add to Google Wallet')}" width="199" height="55" />
					</a>`
				);
			}
			if (badges.length) {
				passBadges = `<div class="aiq-wallet__passes">
					<p class="aiq-wallet__pass-heading">${escapeHtml(
						strings.passHeading || 'Save your membership card'
					)}</p>
					<div class="aiq-wallet__pass-badges">${badges.join('')}</div>
				</div>`;
			}
		}

		return `
			<div class="aiq-wallet__summary">
				${pointsBlock}
				${details.length ? `<dl class="aiq-wallet__details">${details.join('')}</dl>` : ''}
				${renderError()}
				${shopCta}
				${passBadges}
				<button type="button" class="btn btn-primary-outline aiq-wallet__logout" data-aiq-action="logout" ${
					state.loading ? 'disabled' : ''
				}>
					${escapeHtml(strings.logout || 'Sign out')}
				</button>
			</div>
		`;
	}

	function renderUnavailable() {
		return `<p class="aiq-wallet__unavailable">${escapeHtml(
			strings.notConfigured || 'Loyalty wallet is not available right now.'
		)}</p>`;
	}

	function render() {
		let body = '';
		if (state.step === 'unavailable') {
			body = renderUnavailable();
		} else if (state.step === 'pin') {
			body = renderPinStep();
		} else if (state.step === 'wallet') {
			body = renderWalletStep();
		} else {
			body = renderPhoneStep();
		}

		root.innerHTML = `
			<div class="aiq-wallet__inner">
				<h2 class="aiq-wallet__title">${escapeHtml(title)}</h2>
				${body}
			</div>
		`;
	}

	async function handleVerify() {
		setState({ loading: true, error: '', errorCode: '' });
		try {
			await apiRequest('verify', {
				method: 'POST',
				body: { phone: state.phone },
			});
			setState({ step: 'pin', loading: false, code: '' });
		} catch (err) {
			setState({
				loading: false,
				error: err.message || strings.genericError,
				errorCode: err.code || '',
				joinUrl: (err.data && err.data.joinUrl) || state.joinUrl || joinUrl,
			});
		}
	}

	async function handleWallet() {
		setState({ loading: true, error: '', errorCode: '' });
		try {
			const data = await apiRequest('wallet', {
				method: 'POST',
				body: { phone: state.phone, code: state.code },
			});
			setStoredSession(data.sessionToken || '');
			setState({
				step: 'wallet',
				wallet: data.wallet,
				sessionToken: data.sessionToken || '',
				loading: false,
			});
		} catch (err) {
			setState({
				loading: false,
				error: err.message || strings.genericError,
				errorCode: err.code || '',
			});
		}
	}

	async function restoreSession() {
		if (!state.sessionToken || !configured) {
			return;
		}

		setState({ loading: true, error: '' });
		try {
			const data = await apiRequest('session', {
				method: 'GET',
				query: { sessionToken: state.sessionToken },
			});
			setState({
				step: 'wallet',
				wallet: data.wallet,
				sessionToken: data.sessionToken || state.sessionToken,
				loading: false,
			});
		} catch (err) {
			setStoredSession('');
			setState({
				step: 'phone',
				sessionToken: '',
				wallet: null,
				loading: false,
			});
		}
	}

	async function handleLogout() {
		const token = state.sessionToken;
		setState({ loading: true });
		try {
			if (token) {
				await apiRequest('logout', {
					method: 'POST',
					body: { sessionToken: token },
				});
			}
		} catch (e) {
			// still clear local session
		}
		setStoredSession('');
		setState({
			step: 'phone',
			wallet: null,
			sessionToken: '',
			phone: '',
			code: '',
			loading: false,
			error: '',
		});
	}

	root.addEventListener('submit', function (event) {
		const form = event.target.closest('[data-aiq-form]');
		if (!form || !root.contains(form)) {
			return;
		}
		event.preventDefault();

		const formType = form.getAttribute('data-aiq-form');
		if (formType === 'phone') {
			const input = form.querySelector('[name="phone"]');
			const phone = (input && input.value ? input.value : '').trim();
			if (!phone) {
				setState({ error: strings.phoneLabel || 'Enter a phone number.' });
				return;
			}
			state.phone = phone.replace(/\D+/g, '') || phone;
			handleVerify();
			return;
		}

		if (formType === 'pin') {
			const input = form.querySelector('[name="code"]');
			const code = (input && input.value ? input.value : '').replace(/\D+/g, '');
			if (!code) {
				setState({ error: strings.codeLabel || 'Enter the verification code.' });
				return;
			}
			state.code = code;
			handleWallet();
		}
	});

	root.addEventListener('click', function (event) {
		const button = event.target.closest('[data-aiq-action]');
		if (!button || !root.contains(button)) {
			return;
		}

		const action = button.getAttribute('data-aiq-action');
		if (action === 'resend') {
			handleVerify();
		} else if (action === 'back') {
			setState({ step: 'phone', code: '', error: '', errorCode: '' });
		} else if (action === 'logout') {
			handleLogout();
		}
	});

	render();
	restoreSession();
}

document.addEventListener('DOMContentLoaded', function () {
	document.querySelectorAll('[data-aiq-wallet]').forEach(createWalletApp);
});
